import { useEffect, useRef, useState } from "react";
import { api, STAGES, STAGE_LABEL } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, PageBody } from "../components/layout/Page";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Plus, Trash, UserPlus, DownloadSimple, Paperclip, FileArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";

// Show 5 active stages by default (joined hidden until conversion lands them there)
const VISIBLE_STAGES = ["applied", "shortlisted", "interview", "selected", "rejected"];
const POSITIONS = [
  "Junior Accountant",
  "Senior Accountant",
  "Team Leader",
  "BDE Executive Sales",
  "Manager",
  "SEO",
  "Account Officer",
];
const STAGE_COLOR = {
  applied: "border-[#64748B]",
  shortlisted: "border-[#2563EB]",
  interview: "border-[#FAB005]",
  selected: "border-[#12B886]",
  rejected: "border-[#F54236]",
  joined: "border-[#7C3AED]",
};
const SHIFT_OPTIONS = ["", "Morning", "Night"];

export default function Recruitment() {
  const { user } = useAuth();
  const { activeCompanyId, activeCompany } = useCompany();
  const [candidates, setCandidates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", position: "", department: "", experience_years: 0, expected_salary: 0, shift_preference: "", notes: "" });
  const [draggingId, setDraggingId] = useState(null);
  const fileInputs = useRef({});

  const canEdit = ["super_admin", "hr_admin"].includes(user?.role);

  const load = async () => {
    if (!activeCompanyId) return;
    const { data } = await api.get("/recruitment", { params: { company_id: activeCompanyId } });
    setCandidates(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId]);

  const submit = async () => {
    if (!form.full_name || !form.email || !form.position) {
      toast.error("Name, email and position are required");
      return;
    }
    try {
      await api.post("/recruitment", {
        ...form,
        company_id: activeCompanyId,
        stage: "applied",
        experience_years: parseFloat(form.experience_years || 0),
        expected_salary: parseFloat(form.expected_salary || 0),
      });
      toast.success("Candidate added");
      setOpen(false);
      setForm({ full_name: "", email: "", phone: "", position: "", department: "", experience_years: 0, expected_salary: 0, shift_preference: "", notes: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const moveStage = async (id, stage) => {
    const cur = candidates.find((c) => c.id === id);
    if (!cur || cur.stage === stage) return;
    // optimistic update
    setCandidates((prev) => prev.map((c) => (c.id === id ? { ...c, stage } : c)));
    try {
      await api.put(`/recruitment/${id}`, { stage });
      toast.success(`Moved to ${STAGE_LABEL[stage]}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Move failed");
      load();
    }
  };

  const setShift = async (id, shift_preference) => {
    setCandidates((prev) => prev.map((c) => (c.id === id ? { ...c, shift_preference } : c)));
    try {
      await api.put(`/recruitment/${id}`, { shift_preference });
    } catch {
      toast.error("Failed to update shift");
      load();
    }
  };

  const uploadResume = async (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/recruitment/${id}/resume`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Resume uploaded");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
  };

  const downloadResume = async (id, name) => {
    try {
      const res = await api.get(`/recruitment/${id}/resume`, { responseType: "blob" });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = name || "resume.pdf"; a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Resume not available");
    }
  };

  const convert = async (id) => {
    if (!window.confirm("Convert this candidate to an employee?")) return;
    await api.post(`/recruitment/${id}/convert`, { joining_date: new Date().toISOString().slice(0, 10) });
    toast.success("Candidate converted to employee");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this candidate?")) return;
    await api.delete(`/recruitment/${id}`);
    load();
  };

  const exportExcel = async () => {
    try {
      const res = await api.get("/recruitment/export-excel", {
        params: { company_id: activeCompanyId }, responseType: "blob",
      });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "recruitment.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <div data-testid="recruitment-page">
      <PageHeader eyebrow={activeCompany?.code} title="Recruitment" subtitle="Pipeline from applied to joined. Drag candidates between columns.">
        {canEdit && (
          <Button onClick={exportExcel} variant="outline" className="rounded-sm text-xs uppercase tracking-wider" data-testid="export-recruitment-btn">
            <DownloadSimple size={14} className="mr-1.5" /> Excel
          </Button>
        )}
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-xs uppercase tracking-wider" data-testid="add-candidate-btn">
                <Plus size={14} className="mr-1.5" /> Add candidate
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-xl">
              <DialogHeader><DialogTitle>New candidate</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label className="text-xs uppercase">Name</Label><Input value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})} className="rounded-sm" data-testid="cand-name" /></div>
                <div><Label className="text-xs uppercase">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="rounded-sm" data-testid="cand-email" /></div>
                <div><Label className="text-xs uppercase">Phone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="rounded-sm" /></div>
                <div><Label className="text-xs uppercase">Position</Label>
                  <Select value={POSITIONS.includes(form.position) ? form.position : (form.position ? "__custom__" : "")} onValueChange={(v) => {
                    if (v === "__custom__") setForm({...form, position: ""});
                    else setForm({...form, position: v});
                  }}>
                    <SelectTrigger className="rounded-sm" data-testid="cand-position-select"><SelectValue placeholder="Pick a role" /></SelectTrigger>
                    <SelectContent>
                      {POSITIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      <SelectItem value="__custom__">Custom…</SelectItem>
                    </SelectContent>
                  </Select>
                  {!POSITIONS.includes(form.position) && (
                    <Input className="rounded-sm mt-2" placeholder="Enter custom role" value={form.position} onChange={(e) => setForm({...form, position: e.target.value})} data-testid="cand-position" />
                  )}
                </div>
                <div><Label className="text-xs uppercase">Department</Label><Input value={form.department} onChange={(e) => setForm({...form, department: e.target.value})} className="rounded-sm" /></div>
                <div><Label className="text-xs uppercase">Experience (yrs)</Label><Input type="number" value={form.experience_years} onChange={(e) => setForm({...form, experience_years: e.target.value})} className="rounded-sm" /></div>
                <div><Label className="text-xs uppercase">Expected salary</Label><Input type="number" value={form.expected_salary} onChange={(e) => setForm({...form, expected_salary: e.target.value})} className="rounded-sm" /></div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase">Shift preference</Label>
                  <Select value={form.shift_preference || "__none__"} onValueChange={(v) => setForm({...form, shift_preference: v === "__none__" ? "" : v})}>
                    <SelectTrigger className="rounded-sm" data-testid="cand-shift"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      <SelectItem value="Morning">Morning</SelectItem>
                      <SelectItem value="Night">Night</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label className="text-xs uppercase">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="rounded-sm" /></div>
              </div>
              <DialogFooter><Button onClick={submit} className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="save-candidate">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>
      <PageBody>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="kanban-board">
          {VISIBLE_STAGES.map((s) => {
            const items = candidates.filter((c) => c.stage === s);
            return (
              <div
                key={s}
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={(e) => {
                  e.preventDefault();
                  const id = e.dataTransfer.getData("text/plain") || draggingId;
                  if (id) moveStage(id, s);
                  setDraggingId(null);
                }}
                className={`bg-white border-t-2 ${STAGE_COLOR[s]} border-l border-r border-b border-border rounded-sm flex flex-col min-h-[60vh]`}
                data-testid={`column-${s}`}
              >
                <div className="px-3 py-2 border-b border-border flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-semibold">{STAGE_LABEL[s]}</div>
                  <span className="text-xs font-mono text-[#64748B]">{items.length}</span>
                </div>
                <div className="flex-1 p-2 space-y-2 overflow-y-auto scrollbar-thin">
                  {items.map((c) => (
                    <div
                      key={c.id}
                      draggable={canEdit}
                      onDragStart={(e) => { e.dataTransfer.setData("text/plain", c.id); setDraggingId(c.id); }}
                      onDragEnd={() => setDraggingId(null)}
                      className={`border border-border rounded-sm p-3 hover:border-[#2563EB] transition-colors bg-white ${draggingId === c.id ? "opacity-50" : ""} ${canEdit ? "cursor-grab active:cursor-grabbing" : ""}`}
                      data-testid={`candidate-${c.id}`}
                    >
                      <div className="text-sm font-medium text-[#0F172A]">{c.full_name}</div>
                      <div className="text-xs text-[#475569] mt-0.5">{c.position}</div>
                      <div className="text-[10px] text-[#64748B] font-mono mt-1 truncate">{c.email}</div>
                      <div className="text-[10px] text-[#64748B] mt-1">{c.experience_years}y · ₹{c.expected_salary}</div>
                      {canEdit && (
                        <div className="mt-2">
                          <Select value={c.shift_preference || "__none__"} onValueChange={(v) => setShift(c.id, v === "__none__" ? "" : v)}>
                            <SelectTrigger className="rounded-sm h-7 text-[10px] uppercase" data-testid={`cand-shift-${c.id}`}>
                              <SelectValue placeholder="Shift?" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">No preference</SelectItem>
                              <SelectItem value="Morning">Morning</SelectItem>
                              <SelectItem value="Night">Night</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className="mt-2 flex items-center gap-1">
                        {c.resume_file_name ? (
                          <button onClick={() => downloadResume(c.id, c.resume_file_name)} className="text-[10px] text-[#2563EB] hover:underline flex items-center gap-0.5" data-testid={`resume-dl-${c.id}`}>
                            <FileArrowDown size={11}/> {c.resume_file_name.length > 18 ? c.resume_file_name.slice(0,15)+"…" : c.resume_file_name}
                          </button>
                        ) : (
                          <span className="text-[10px] text-[#94A3B8]">No resume</span>
                        )}
                        {canEdit && (
                          <>
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx"
                              ref={(el) => { fileInputs.current[c.id] = el; }}
                              onChange={(e) => { uploadResume(c.id, e.target.files?.[0]); e.target.value = ""; }}
                              className="hidden"
                              data-testid={`resume-input-${c.id}`}
                            />
                            <button
                              onClick={() => fileInputs.current[c.id]?.click()}
                              className="ml-auto p-0.5 hover:bg-[#EFF6FF] text-[#2563EB] rounded-sm"
                              title="Upload resume"
                              data-testid={`resume-upload-${c.id}`}
                            >
                              <Paperclip size={12} />
                            </button>
                          </>
                        )}
                      </div>
                      {canEdit && (
                        <div className="flex items-center gap-1 mt-2 pt-2 border-t border-border">
                          {c.stage === "selected" && (
                            <button onClick={() => convert(c.id)} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[#2563EB] text-white rounded-sm flex items-center gap-1" data-testid={`convert-${c.id}`}>
                              <UserPlus size={10} /> Hire
                            </button>
                          )}
                          <button onClick={() => remove(c.id)} className="ml-auto p-0.5 hover:bg-[#F54236]/10 text-[#F54236] rounded-sm" data-testid={`delete-cand-${c.id}`}><Trash size={11} /></button>
                        </div>
                      )}
                    </div>
                  ))}
                  {items.length === 0 && <div className="text-xs text-[#64748B] text-center py-4">Drop candidates here</div>}
                </div>
              </div>
            );
          })}
        </div>
        {canEdit && (
          <div className="mt-3 text-[11px] text-[#64748B]">
            Tip: drag a candidate card and drop it on another column to update their stage.
          </div>
        )}
      </PageBody>
    </div>
  );
}
